import type { SidewallSvgThumbnailProps } from "../types";
import {
  PANEL_POSITIONS,
  SIDEWALL_DIMENSIONS,
  EMPTY_POSITION_COLOR,
  SVG_UI_COLORS,
  SVG_THUMBNAIL_RADII,
  SVG_THUMBNAIL_ANGLES,
  SVG_THUMBNAIL_LAYOUT,
  SVG_THUMBNAIL_FONTS,
} from "../constants";
import { getComponentColor, SidewallSvgColors } from "../../../theme/colors";
import { SPECIAL_POSITION_IDS } from "../../../models";
// Helper to convert degrees to radians
const deg2rad = (deg: number) => (deg * Math.PI) / 180;

// Archway angles (derived from constants)
const ARCHWAY_START_ANGLE = deg2rad(SVG_THUMBNAIL_ANGLES.ARCHWAY_START);
const ARCHWAY_END_ANGLE = deg2rad(SVG_THUMBNAIL_ANGLES.ARCHWAY_END);

// Slag Door uses same angles as Archway (surrounds it)
const SLAG_DOOR_START_ANGLE = ARCHWAY_START_ANGLE;
const SLAG_DOOR_END_ANGLE = ARCHWAY_END_ANGLE;

// Sump positions for SVG (narrower than 3D model - 30 degrees each, centered at 270°)
// Total: 90 degrees (225° to 315°), no gaps between sumps
const SUMP_SVG_POSITIONS = [
  {
    positionId: SPECIAL_POSITION_IDS.LEFT_SUMP,
    startAngle: deg2rad(SVG_THUMBNAIL_ANGLES.SUMP_LEFT_START),
    endAngle: deg2rad(SVG_THUMBNAIL_ANGLES.SUMP_LEFT_END),
  },
  {
    positionId: SPECIAL_POSITION_IDS.RIGHT_SUMP,
    startAngle: deg2rad(SVG_THUMBNAIL_ANGLES.SUMP_RIGHT_START),
    endAngle: deg2rad(SVG_THUMBNAIL_ANGLES.SUMP_RIGHT_END),
  },
  {
    positionId: SPECIAL_POSITION_IDS.CENTER_SUMP,
    startAngle: deg2rad(SVG_THUMBNAIL_ANGLES.SUMP_CENTER_START),
    endAngle: deg2rad(SVG_THUMBNAIL_ANGLES.SUMP_CENTER_END),
  },
];

// Helper to create SVG arc path
const createArcPath = (
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  startAngle: number,
  endAngle: number,
  scale: number,
): string => {
  const ir = innerRadius * scale;
  const or = outerRadius * scale;

  let effectiveEndAngle = endAngle;
  if (endAngle < startAngle) {
    effectiveEndAngle = endAngle + Math.PI * 2;
  }

  const startInnerX = cx + Math.cos(startAngle) * ir;
  const startInnerY = cy + Math.sin(startAngle) * ir;
  const startOuterX = cx + Math.cos(startAngle) * or;
  const startOuterY = cy + Math.sin(startAngle) * or;

  const endInnerX = cx + Math.cos(effectiveEndAngle) * ir;
  const endInnerY = cy + Math.sin(effectiveEndAngle) * ir;
  const endOuterX = cx + Math.cos(effectiveEndAngle) * or;
  const endOuterY = cy + Math.sin(effectiveEndAngle) * or;

  const arcSpan = effectiveEndAngle - startAngle;
  const largeArcFlag = arcSpan > Math.PI ? 1 : 0;

  return `
    M ${startInnerX} ${startInnerY}
    A ${ir} ${ir} 0 ${largeArcFlag} 1 ${endInnerX} ${endInnerY}
    L ${endOuterX} ${endOuterY}
    A ${or} ${or} 0 ${largeArcFlag} 0 ${startOuterX} ${startOuterY}
    Z
  `;
};

// Fixed internal size for consistent calculations - actual display size controlled by width/height
const INTERNAL_SIZE = 200;

export const SidewallSvgThumbnail = ({
  sidewallData,
  size = 80,
  showNumbers = true,
}: SidewallSvgThumbnailProps) => {
  // Use fixed internal coordinates for stable rendering
  const cx = INTERNAL_SIZE / 2;
  const cy = INTERNAL_SIZE / 2;
  // Scale factor accounts for sump housing protruding beyond outer ring
  const scale = (INTERNAL_SIZE / 2) * SVG_THUMBNAIL_LAYOUT.SCALE_FACTOR;

  const locationDataByPositionId = new Map<number, boolean>();

  for (const location of sidewallData.locations) {
    // Burner sub-components have ParentBurnerPositionId set
    // Their PositionId (1-4) represents position WITHIN the burner panel, not on the sidewall
    // So we should NOT set their PositionId in the map (it would conflict with panels 1-4)
    // Instead, we only mark the parent burner position as "has part"
    if (location.ParentBurnerPositionId) {
      if (location.HasAssignedPart) {
        locationDataByPositionId.set(location.ParentBurnerPositionId, true);
      }
    } else if (location.PositionId) {
      // Regular positions (panels, sumps, shells, etc.)
      locationDataByPositionId.set(
        location.PositionId,
        location.HasAssignedPart ?? false,
      );
    }
  }

  const getPositionColor = (positionId: number): string => {
    const hasAssignedPart = locationDataByPositionId.get(positionId) ?? false;
    if (!hasAssignedPart) {
      return EMPTY_POSITION_COLOR;
    }
    return getComponentColor(positionId);
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${INTERNAL_SIZE} ${INTERNAL_SIZE}`}
    >
      {/* Ring around panels (shell) - rendered first as background */}
      {(() => {
        const innerR = SIDEWALL_DIMENSIONS.panelOuterRadius * scale;
        const outerR = SVG_THUMBNAIL_RADII.RING_OUTER * scale;
        return (
          <>
            {/* Ring fill as donut shape */}
            <path
              d={`
                M ${cx + outerR} ${cy}
                A ${outerR} ${outerR} 0 1 1 ${cx - outerR} ${cy}
                A ${outerR} ${outerR} 0 1 1 ${cx + outerR} ${cy}
                M ${cx + innerR} ${cy}
                A ${innerR} ${innerR} 0 1 0 ${cx - innerR} ${cy}
                A ${innerR} ${innerR} 0 1 0 ${cx + innerR} ${cy}
              `}
              fill={getPositionColor(SPECIAL_POSITION_IDS.UPPER_SHELL)}
              fillRule="evenodd"
            />
            {/* Outer border */}
            <circle
              cx={cx}
              cy={cy}
              r={outerR}
              fill="none"
              stroke={SVG_UI_COLORS.STROKE}
              strokeWidth={SVG_THUMBNAIL_LAYOUT.STROKE_WIDTH}
            />
          </>
        );
      })()}

      {/* Lower Shell (center area) */}
      <circle
        cx={cx}
        cy={cy}
        r={scale * SIDEWALL_DIMENSIONS.panelInnerRadius}
        fill={getPositionColor(SPECIAL_POSITION_IDS.LOWER_SHELL)}
        stroke={SVG_UI_COLORS.STROKE}
        strokeWidth={SVG_THUMBNAIL_LAYOUT.STROKE_WIDTH}
      />

      {/* Panels (1-12) */}
      {PANEL_POSITIONS.map((position) => {
        const color = getPositionColor(position.positionId);
        const path = createArcPath(
          cx,
          cy,
          position.innerRadius,
          position.outerRadius,
          position.startAngle,
          position.endAngle,
          scale,
        );

        return (
          <path
            key={`panel-${position.positionId}`}
            d={path}
            fill={color}
            stroke={SVG_UI_COLORS.STROKE}
            strokeWidth={SVG_THUMBNAIL_LAYOUT.STROKE_WIDTH}
          />
        );
      })}

      {/* Sumps (protruding outside the ring) */}
      {SUMP_SVG_POSITIONS.map((position) => {
        const color = getPositionColor(position.positionId);
        const path = createArcPath(
          cx,
          cy,
          SVG_THUMBNAIL_RADII.RING_OUTER,
          SVG_THUMBNAIL_RADII.SUMP_OUTER,
          position.startAngle,
          position.endAngle,
          scale,
        );

        return (
          <path
            key={`sump-${position.positionId}`}
            d={path}
            fill={color}
            stroke={SVG_UI_COLORS.STROKE}
            strokeWidth={SVG_THUMBNAIL_LAYOUT.STROKE_WIDTH}
          />
        );
      })}

      {/* Archway (opposite to sumps, at top) */}
      <path
        d={createArcPath(
          cx,
          cy,
          SVG_THUMBNAIL_RADII.RING_OUTER,
          SVG_THUMBNAIL_RADII.SUMP_OUTER,
          ARCHWAY_START_ANGLE,
          ARCHWAY_END_ANGLE,
          scale,
        )}
        fill={getPositionColor(SPECIAL_POSITION_IDS.ARCHWAY)}
        stroke={SVG_UI_COLORS.STROKE}
        strokeWidth={SVG_THUMBNAIL_LAYOUT.STROKE_WIDTH}
      />

      {/* Slag Door (surrounds archway at top) */}
      <path
        d={createArcPath(
          cx,
          cy,
          SVG_THUMBNAIL_RADII.SUMP_OUTER,
          SVG_THUMBNAIL_RADII.SLAG_DOOR_OUTER,
          SLAG_DOOR_START_ANGLE,
          SLAG_DOOR_END_ANGLE,
          scale,
        )}
        fill={getPositionColor(SPECIAL_POSITION_IDS.SLAG_DOOR)}
        stroke={SVG_UI_COLORS.STROKE}
        strokeWidth={SVG_THUMBNAIL_LAYOUT.STROKE_WIDTH}
      />

      {/* Burner indicators */}
      {[3, 5, 9].map((burnerPosition) => {
        const panel = PANEL_POSITIONS.find(
          (p) => p.positionId === burnerPosition,
        );
        if (!panel) return null;

        const midAngle = (panel.startAngle + panel.endAngle) / 2;
        // Position burners closer to the center
        const midRadius =
          (panel.innerRadius +
            (panel.outerRadius - panel.innerRadius) *
              SVG_THUMBNAIL_LAYOUT.BURNER_POSITION_RATIO) *
          scale;
        const bx = cx + Math.cos(midAngle) * midRadius;
        const by = cy + Math.sin(midAngle) * midRadius;

        return (
          <circle
            key={`burner-${burnerPosition}`}
            cx={bx}
            cy={by}
            r={SVG_THUMBNAIL_LAYOUT.BURNER_INDICATOR_RADIUS}
            fill={SidewallSvgColors.BURNER_FILL}
            stroke={SidewallSvgColors.BURNER_STROKE}
            strokeWidth={SVG_THUMBNAIL_LAYOUT.STROKE_WIDTH}
          />
        );
      })}

      {/* Panel numbers (optional) */}
      {showNumbers &&
        PANEL_POSITIONS.map((position) => {
          const midAngle = (position.startAngle + position.endAngle) / 2;
          // Position numbers closer to the outer edge
          const midRadius =
            (position.innerRadius +
              (position.outerRadius - position.innerRadius) *
                SVG_THUMBNAIL_LAYOUT.LABEL_POSITION_RATIO) *
            scale;
          const tx = cx + Math.cos(midAngle) * midRadius;
          const ty = cy + Math.sin(midAngle) * midRadius;

          return (
            <text
              key={`label-${position.positionId}`}
              x={tx}
              y={ty}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={SidewallSvgColors.PANEL_TEXT}
              fontSize={
                size > SVG_THUMBNAIL_FONTS.SIZE_THRESHOLD
                  ? SVG_THUMBNAIL_FONTS.LARGE_SIZE
                  : SVG_THUMBNAIL_FONTS.SMALL_SIZE
              }
              fontWeight="bold"
            >
              {position.positionId}
            </text>
          );
        })}
    </svg>
  );
};
